import { useEffect, useState, useCallback } from "react";
import { useStytchMemberSession, useStytchB2BClient } from "@stytch/react/b2b";
import { useNavigate } from "react-router-dom";
import { Spinner } from "@/components/ui/spinner";
import useQueryStringParams from "../../hooks/useQueryStringParams";
import { trpc } from "../../trpc";
import posthog from "../../lib/posthog";
import { DEFAULT_LANDING_PATH } from "../../utils/routes";

const Authenticate: React.FC = (): JSX.Element => {
  const stytch = useStytchB2BClient();
  const { session } = useStytchMemberSession();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [hasFetchedMerchant, setHasFetchedMerchant] = useState<boolean>(false);

  const { slug, token } = useQueryStringParams(["slug", "token"]);

  const merchant = trpc.merchants.get.useQuery(
    {
      id: slug || "",
    },
    {
      enabled: !!slug,
      onSuccess: () => {
        setHasFetchedMerchant(true);
      },
      onError: () => {
        setHasFetchedMerchant(true);
      },
    }
  );

  const redirectToSignup = useCallback(() => {
    navigate('/signup');
  }, [navigate]);

  useEffect(() => {
    const authenticateUser = async () => {
      if (session) {
        navigate(DEFAULT_LANDING_PATH);
        return;
      }

      if (token) {
        try {
          const authResult = await stytch.magicLinks.authenticate({
            magic_links_token: token,
            session_duration_minutes: merchant.data?.sessionDurationMinutes || 259200,
          });
          const memberId = authResult.member.member_id;
          const email = authResult.member.email_address;
          posthog.identify(memberId, { email, merchant_id: slug });
          posthog.capture("user authenticated", { email, merchant_id: slug });
          navigate(DEFAULT_LANDING_PATH);
        } catch (error) {
          console.error("Authentication error:", error);
          posthog.captureException(error);
          setError("Failed to authenticate. Please try again.");
          redirectToSignup();
        }
      } else {
        redirectToSignup();
      }
    };

    if (hasFetchedMerchant) {
      authenticateUser();
    }
  }, [stytch, session, navigate, slug, token, redirectToSignup, merchant.data, hasFetchedMerchant]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 py-8">
        <p className="text-red-500">{error}</p>
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center py-8">
      <Spinner size="xl" />
    </div>
  );
};

export default Authenticate;
